(() => {
  // Companion to name-necklace-switcher-v3.js. It adds no behaviour of its own: it makes the
  // gallery, the cart and the address bar agree with the metal the customer has chosen, and it
  // never lets a wrong frame paint.
  //
  // THE JUMP. The switcher picks a slide index from the copy of the product it fetched on page
  // load. A new name owns ONE photo; its other colours arrive seconds later and the Worker then
  // reorders the gallery so the three colours sit at 0,1,2. The switcher still holds the old
  // order, so a metal change drives the slider to an index that now belongs to something else -
  // usually a model wearing a necklace. Correcting that afterwards still let the wrong frame
  // paint, which is the whole complaint, so this INTERCEPTS: every gallery movement goes through
  // slick's slickGoTo, and slickGoTo is wrapped. For seven seconds after a metal change or a
  // product swap, any call is redirected to the slide that genuinely holds that colour.
  //
  // THE YELLOW DEFAULT. When the customer types a name we already sell, the switcher swaps the
  // product in and always settles on slide 0 - the yellow shot - whatever metal was selected.
  // Measured: White Gold selected, typed "Lia", got the yellow necklace. A swap now opens the
  // same window as a metal change.
  //
  // FINDING THE RIGHT SLIDE asks Shopify rather than guessing: the variant's own featured image.
  // gold-name-necklace-lia is hand-built with generic alt text ("image 1", "image 2"), so alts
  // are useless there - but its 14K White Gold variant points at image 2. 18K and Platinum carry
  // no image of their own and fall back to the matching 14K colour. Alt text is the last resort,
  // for a generated name whose variants are not linked yet.
  //
  // WHAT IT MUST NEVER TOUCH. Only img.rimage__img, and never anything inside
  // .product-gallery__sale-ribbon. An earlier selector here was `.slick-active img`, which
  // overwrote the SALE ribbon graphic with the necklace photo - it rendered as a small stray
  // necklace in the top-right of the slide. Also: the theme lazy-loads the gallery, so every
  // slide but the first ships a 1x1 GIF as src with the real file in data-master. Judge slides by
  // data-master when present and ignore data: URIs; an even earlier version judged by src, called
  // nine correct slides foreign on every page load and repainted them, killing lazy loading.

  const HANDLE_RE = /^\/products\/([^/?#]+)/;
  const handleFromUrl = () => (location.pathname.match(HANDLE_RE) || [])[1] || '';
  const theForm = () => document.querySelector('form[action*="/cart/add"]');
  const idInput = () => theForm()?.querySelector('[name="id"], [name="variant_id"]');
  const METALS = ['14K Yellow Gold', '14K White Gold', '14K Rose Gold'];
  const METAL_KEY = (m) => (/rose/i.test(m) ? 'rose gold' : /white|platinum/i.test(m) ? 'white gold' : 'yellow gold');
  const BASE_METAL = (m) => (/rose/i.test(m) ? '14K Rose Gold' : /white|platinum/i.test(m) ? '14K White Gold' : '14K Yellow Gold');
  const PHOTO = 'img.rimage__img';
  const isRibbon = (el) => !!el.closest('.product-gallery__sale-ribbon');

  const stem = (url) => {
    let s = String(url || '').split('?')[0].split('/').pop() || '';
    s = s.replace(/\.(jpg|jpeg|png|webp|gif|avif)$/i, '');
    s = s.replace(/\.progressive$/i, '');
    s = s.replace(/_\{width\}x$/i, '');
    s = s.replace(/_\d+x\d*$/i, '');
    return s.toLowerCase();
  };
  const slideKey = (el) => {
    const master = el.getAttribute('data-master');
    if (master) return stem(master);
    const src = el.currentSrc || el.getAttribute('src') || '';
    if (!src || /^data:/i.test(src)) return '';
    return stem(src);
  };

  let current = null;
  const cache = new Map();
  // The Worker serves the same JSON shape straight from the Admin API, with no edge cache.
  // Measured 2026-09-04 (Memphis, first metal switch ~60s after creation): the storefront copy
  // was still a cached 404, so nothing below could run - the cart variant sat EMPTY and the
  // white-gold slide invisible for ~10s until Shopify's cache caught up.
  const workerOrigin = () => { try { return new URL((window.SegalNameNecklace || {}).requestUrl).origin; } catch { return ''; } };
  const workerProduct = async (handle) => {
    const o = workerOrigin(); if (!o) return null;
    const r = await fetch(`${o}/product/${encodeURIComponent(handle)}?_=${Date.now()}`, { cache: 'no-store' });
    return r.ok ? r.json() : null;
  };
  const load = async (handle, fresh) => {
    if (!fresh && cache.has(handle)) return cache.get(handle);
    // cache:'no-store' only bypasses the BROWSER cache. Shopify edge-caches /products/x.js for
    // a while, so a "fresh" read straight after the Worker says 'created' came back without the
    // new colour (measured on gold-name-necklace-tamsin: 'created' at 20:39:47, fresh JSON still
    // yellow-only at 20:39:48). A unique query string is a different cache key at the edge.
    const bust = fresh ? `?_=${Date.now()}` : '';
    let p = null;
    try {
      const r = await fetch(`/products/${encodeURIComponent(handle)}.js${bust}`, { credentials: 'same-origin', cache: 'no-store' });
      if (r.ok) p = await r.json();
    } catch (_) { p = null; }
    if (fresh || !p) {
      const w = await workerProduct(handle).catch(() => null);
      if (w && (!p || (w.media || []).length >= (p.media || []).length)) p = w;
    }
    if (!p) throw new Error('product unavailable');
    cache.set(handle, p);
    if (handle === handleFromUrl()) current = p;
    return p;
  };

  const colourEndpoint = () => {
    try { return `${new URL((window.SegalNameNecklace || {}).requestUrl).origin}/name-colour`; }
    catch { return ''; }
  };
  const isGenerated = (p) => (p?.media || []).some((m) => String(m.alt || '').includes('name necklace in solid'));
  const missingColours = (p) => {
    if (!isGenerated(p)) return [];
    const alts = (p.media || []).map((m) => String(m.alt || ''));
    return METALS.filter((m) => !alts.some((a) => a.includes(`solid ${m},`)));
  };

  // A colour that has just LANDED must be shown, not merely fetched.
  //
  // Measured 2026-09-02 on a fresh gold-name-necklace-orlie: the customer picked 14K White
  // Gold seconds after creation; this function asked the Worker for it and the Worker
  // delivered (white shot on the product, variant linked, ~50s). The page kept showing the
  // yellow photo for the entire 45s sampling window and beyond, because the only thing that
  // moves the gallery is the 7-second metalIntent window opened at the moment of the click,
  // and the colour arrives long after it has closed. So the necklace was ready and correct on
  // Shopify and wrong on screen - the exact thing Yaniv keeps hitting.
  //
  // Now: each colour is requested on its own; the moment one comes back 'created' or
  // 'exists', the product JSON is refreshed and, if it is the colour the customer is looking
  // at, the intent window is re-opened and the gallery settled onto it. While it is on the
  // way, the copy under the name says so instead of the photo sitting silently on yellow.
  // Its own element: name-necklace-ui.js rewrites .snui__s on every metal change, so a note
  // written there was gone before anyone saw it.
  const noteEl = () => {
    const box = document.querySelector('.snui');
    if (!box) return null;
    let el = box.querySelector('.snui__pending');
    if (!el) {
      el = document.createElement('div');
      el.className = 'snui__pending';
      el.style.cssText = 'font-size:13px;line-height:1.4;color:#8a6a2b;margin-top:9px;display:none';
      box.appendChild(el);
    }
    return el;
  };
  const showPending = (metal) => {
    const el = noteEl();
    if (!el) return;
    el.textContent = `Preparing your necklace in ${metal} — about 20 seconds. The photo updates by itself.`;
    el.style.display = 'block';
  };
  const clearPending = () => { const el = noteEl(); if (el) el.style.display = 'none'; };

  const asked = new Set();
  const completeColours = async (handle, product) => {
    const endpoint = colourEndpoint();
    if (!endpoint) return;
    const missing = missingColours(product).filter((m) => !asked.has(`${handle}:${m}`));
    if (!missing.length) return;
    missing.forEach((m) => asked.add(`${handle}:${m}`));
    const M = window.SegalNameNecklaceMetrics = window.SegalNameNecklaceMetrics || {};
    M.lastColourPrefetch = { handle, requested: missing, startedAt: new Date().toISOString() };

    const viewing = BASE_METAL(visibleMetal());
    if (missing.includes(viewing)) showPending(visibleMetal());

    const hasColour = (p, metal) => (p?.media || []).some((n) => String(n.alt || '').includes(`solid ${metal},`));
    // Paint one URL into whatever main slide is on screen. Deterministic: no slider movement.
    const paintActive = (src) => {
      let changed = false;
      document.querySelectorAll(`.product-gallery__main_item.slick-active ${PHOTO}`).forEach((img) => {
        if (isRibbon(img)) return;
        if (stem(img.currentSrc || img.src) === stem(src)) return;
        img.src = src; img.srcset = src; img.removeAttribute('data-master');
        img.classList.remove('lazyload', 'lazyload--hold');
        changed = true;
      });
      return changed;
    };

    const landed = async (metal, url) => {
      // FAST PATH. The Worker now answers with the CDN URL of the shot it just made, so the
      // page paints the instant the render is done (measured 16s) instead of waiting ~30s for
      // Shopify's storefront cache to admit the image exists. Re-asserted for a few seconds
      // because the theme's lazy-loader can put the old src back on a slide it re-enters.
      if (url && handle === handleFromUrl() && BASE_METAL(visibleMetal()) === metal) {
        clearPending();
        [0, 300, 900, 1800, 3000, 4500, 7000].forEach((d) => setTimeout(() => {
          if (handle !== handleFromUrl() || BASE_METAL(visibleMetal()) !== metal) return;
          T('paint-url', { metal, d, changed: paintActive(url) });
        }, d));
      }
      // SLOW PATH, still needed: refresh the product JSON so later metal changes resolve to the
      // right slide. The Worker's 'created' precedes the storefront JSON by up to ~30s.
      let fresh = null;
      let polls = 0;
      for (let n = 0; n < 30; n += 1) {
        if (handle !== handleFromUrl()) return;      // customer has moved on
        polls += 1;
        try { fresh = await load(handle, true); } catch (_) { fresh = null; }
        if (fresh && hasColour(fresh, metal)) break;
        fresh = null;
        await new Promise((r) => setTimeout(r, 2000));
      }
      T('poll-done', { metal, polls, found: !!fresh });
      if (!fresh) return;
      current = fresh;
      M.lastColourLanded = { handle, metal, at: new Date().toISOString() };
      if (BASE_METAL(visibleMetal()) !== metal) { T('not-viewing', { metal, viewing: visibleMetal() }); return; }
      clearPending();
      // Paint the slide that is on screen with the colour's own photo. Moving slick to another
      // index shuffles a gallery whose order changed underneath it (measured: it landed on a
      // lifestyle shot). Writing the src into the active slide is deterministic.
      metalIntent = { metal: visibleMetal(), at: Date.now(), why: 'colour-landed' };
      [0, 300, 900, 1800, 3000, 4500].forEach((d) => setTimeout(() => {
        if (BASE_METAL(visibleMetal()) !== metal) return;
        patchSliders();
        const want = wantedSlide();
        const changed = enforceActiveImage();
        T('paint', { metal, d, want, changed, active: document.querySelectorAll('.product-gallery__main_item.slick-active img.rimage__img').length });
      }, d));
    };

    const trace = M.colourTrace = M.colourTrace || [];
    const T = (ev, extra) => trace.push(Object.assign({ t: new Date().toISOString().slice(11, 23), ev }, extra || {}));
    await Promise.all(missing.map(async (metal) => {
      let status = '', url = '';
      const sent = Date.now();
      T('request', { metal });
      try {
        const r = await fetch(endpoint, {
          method: 'POST', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ handle, metal })
        });
        const body = await r.json().catch(() => ({}));
        status = body.status || ''; url = body.url || '';
      } catch (_) { status = ''; }
      T('response', { metal, status, ms: Date.now() - sent, url: !!url });
      if (status === 'created' || status === 'exists') await landed(metal, url);
    }));
    clearPending();
    try {
      const fresh = await load(handle, true);
      M.lastColourPrefetch = { handle, requested: missing,
        nowHas: METALS.filter((x) => (fresh.media || []).some((n) => String(n.alt || '').includes(`solid ${x},`))),
        finishedAt: new Date().toISOString() };
    } catch (_) { /* sweep backstop */ }
  };

  const visibleMetal = () => {
    for (const s of document.querySelectorAll('#variant-selector-option-0, select.single-option-selector')) {
      const label = String(s.selectedOptions?.[0]?.textContent || '').trim();
      if (label) return label;
    }
    return '';
  };

  const realIndexOf = (el, domIndex) => {
    const slide = el.closest ? el.closest('.slick-slide') : null;
    if (!slide) return domIndex;
    const raw = parseInt(slide.getAttribute('data-slick-index'), 10);
    if (Number.isNaN(raw)) return domIndex;
    const slider = slide.closest('.slick-slider');
    const count = slider ? slider.querySelectorAll('.slick-slide:not(.slick-cloned)').length : 0;
    if (!count) return raw;
    return ((raw % count) + count) % count;
  };

  const sliderEls = () => ['.product-gallery__main_slider', '.product-gallery__thumbnail_slider', '.product-gallery__fullscreen-thumbnail_slider']
    .map((sel) => document.querySelector(sel))
    .filter((el) => el && el.classList.contains('slick-initialized') && el.slick && typeof el.slick.slickGoTo === 'function');

  const metalSlide = (product, metal) => {
    if (!product || !metal) return -1;
    const variants = product.variants || [];
    for (const candidate of [metal, BASE_METAL(metal)]) {
      const v = variants.find((x) => x.title === candidate);
      const pos = v && v.featured_image && v.featured_image.position;
      if (pos > 0) return pos - 1;
    }
    const label = METAL_KEY(metal);
    const media = product.media || [];
    for (let i = 0; i < media.length; i += 1) {
      if (String(media[i].alt || '').toLowerCase().includes(`solid 14k ${label},`)) return i;
    }
    return -1;
  };

  let metalIntent = null;
  const intentActive = () => metalIntent && Date.now() - metalIntent.at < 7000;
  const openIntent = (why) => {
    const label = visibleMetal();
    if (label) metalIntent = { metal: label, at: Date.now(), why };
  };
  const wantedSlide = () => (intentActive() && current ? metalSlide(current, metalIntent.metal) : -1);

  const patchSliders = () => {
    sliderEls().forEach((el) => {
      const api = el.slick;
      if (api.__segalPatched) return;
      api.__segalPatched = true;
      const original = api.slickGoTo.bind(api);
      api.slickGoTo = (index, dontAnimate) => {
        const want = wantedSlide();
        return original(want >= 0 ? want : index, dontAnimate);
      };
    });
  };

  const enforceActiveImage = () => {
    const want = wantedSlide();
    if (want < 0 || !current) return false;
    const src = (current.images || [])[want];
    if (!src) return false;
    let changed = false;
    document.querySelectorAll(`.product-gallery__main_item.slick-active ${PHOTO}`).forEach((img) => {
      if (isRibbon(img)) return;
      if (stem(img.currentSrc || img.src) === stem(src)) return;
      img.src = src; img.srcset = src; img.removeAttribute('data-master');
      img.classList.remove('lazyload', 'lazyload--hold');
      changed = true;
    });
    return changed;
  };

  const settle = () => {
    [60, 200, 450, 900, 1600, 2600, 3600].forEach((d) => setTimeout(() => {
      patchSliders();
      const want = wantedSlide();
      if (want < 0) return;
      sliderEls().forEach((el) => { try { if (el.slick.currentSlide !== want) el.slick.slickGoTo(want, true); } catch (_) {} });
      enforceActiveImage();
    }, d));
  };

  // The switcher rewrites ?variant= on a metal change but leaves ?firstOptionValue= holding the
  // metal the page was FIRST loaded with, so the two contradict each other - and on a reload the
  // theme believes firstOptionValue. Measured on gold-name-necklace-lia: switch Yellow -> White
  // and the address bar reads ?firstOptionValue=14K+Yellow+Gold&variant=<the White id>. Reload,
  // share or press back and it all silently reverts to Yellow, cart variant included.
  const syncUrl = (product) => {
    const url = new URL(location.href);
    if (!url.searchParams.has('firstOptionValue') && !url.searchParams.has('variant')) return false;
    const metal = visibleMetal();
    if (!metal) return false;
    const variant = (product.variants || []).find((v) => v.title === metal);
    let changed = false;
    if (url.searchParams.has('firstOptionValue') && url.searchParams.get('firstOptionValue') !== metal) {
      url.searchParams.set('firstOptionValue', metal); changed = true;
    }
    if (variant && url.searchParams.get('variant') !== String(variant.id)) {
      url.searchParams.set('variant', String(variant.id)); changed = true;
    }
    if (changed) history.replaceState({}, '', url);
    return changed;
  };

  const fixVariant = (product) => {
    const node = idInput();
    if (!node) return false;
    const ids = (product.variants || []).map((v) => String(v.id));
    if (ids.includes(String(node.value || ''))) return false;
    const wanted = visibleMetal();
    const match = (product.variants || []).find((v) => v.title === wanted) || (product.variants || [])[0];
    if (!match) return false;
    node.value = match.id;
    const select = theForm()?.querySelector('select[name="id"]');
    if (select) {
      [...select.options].forEach((o) => {
        const m = (product.variants || []).find((v) => v.title === o.textContent.trim());
        if (m) o.value = m.id;
      });
      select.value = match.id;
    }
    return true;
  };

  let busy = false;
  let lastHandle = '';
  const check = async () => {
    patchSliders();
    if (busy) return;
    const handle = handleFromUrl();
    if (!handle) return;
    busy = true;
    try {
      const swapped = !!lastHandle && handle !== lastHandle;
      lastHandle = handle;
      const product = await load(handle, swapped || intentActive());
      current = product;

      if (swapped) { openIntent('swap'); settle(); }

      const fixed = [];
      if (fixVariant(product)) fixed.push('variant');
      if (syncUrl(product)) fixed.push('url');

      const images = product.images || [];
      const own = new Set(images.map(stem));
      if (own.size > 1) {
        let repainted = 0;
        document.querySelectorAll(`.product-gallery__main_item ${PHOTO}, .product-gallery__thumbnail_item ${PHOTO}, .product-gallery__thumbnail ${PHOTO}`)
          .forEach((el, domIndex) => {
            if (isRibbon(el)) return;
            const key = slideKey(el);
            if (!key || own.has(key)) return;
            const src = images[realIndexOf(el, domIndex)] || images[0];
            if (!src) return;
            el.src = src; el.srcset = src; el.removeAttribute('data-master');
            el.classList.remove('lazyload', 'lazyload--hold');
            repainted += 1;
          });
        if (repainted) fixed.push(`gallery:${repainted}`);
      }

      const want = wantedSlide();
      if (want >= 0) {
        const els = sliderEls();
        if (els.length && els[0].slick.currentSlide !== want) {
          els.forEach((el) => { try { el.slick.slickGoTo(want, true); el.slick.setPosition(); } catch (_) {} });
          fixed.push(`slide:${want}`);
        }
        if (enforceActiveImage()) fixed.push('activeimg');
      }

      const name = String(product.title || '').split('–')[1]?.split('|')[0]?.trim();
      if (name && !document.title.includes(name)) {
        document.title = `${product.title} – Segal Jewelry`;
        fixed.push('title');
      }

      if (fixed.length) {
        const M = window.SegalNameNecklaceMetrics = window.SegalNameNecklaceMetrics || {};
        M.lastGuardFix = { handle, fixed, why: metalIntent?.why || null, measuredAt: new Date().toISOString() };
      }
      completeColours(handle, product);
    } catch (_) { /* a guard must never break the page */ }
    finally { busy = false; }
  };

  // TIMER-FREE CORRECTION. The theme's own option_selection.js answers a metal change by
  // looking the variant up in the product it was SERVER-RENDERED with, then writes that id into
  // the form and the address bar. After a swap that is the wrong product: the id it finds is a
  // Bettina variant, and because the switcher already rewrote the hidden select's option values
  // to the new product's ids, no option matches and the form's variant becomes EMPTY. Measured
  // 2026-09-04 (Marisela, first metal switch after the swap): form id "", URL on Bettina's
  // white variant, gallery slide invisible - for as long as the polling below took to notice.
  // The theme announces the moment it is done by calling history.replaceState with ?variant=,
  // so that call is wrapped: an id that does not belong to the product on screen is replaced,
  // synchronously, with the right one for the metal the customer picked, and the form is fixed
  // in the same breath. No timer, no cache, no race.
  const origReplaceState = history.replaceState.bind(history);
  history.replaceState = function (state, title, url) {
    try {
      if (current && (current.variants || []).length && url != null) {
        const u = new URL(String(url), location.href);
        const v = u.searchParams.get('variant');
        if (v && u.pathname === location.pathname && !current.variants.some((x) => String(x.id) === v)) {
          const metal = visibleMetal();
          const match = current.variants.find((x) => x.title === metal) || current.variants[0];
          if (match) {
            u.searchParams.set('variant', String(match.id));
            url = u.toString();
            const node = idInput(); if (node) node.value = match.id;
            const sel = theForm()?.querySelector('select[name="id"]'); if (sel) sel.value = match.id;
            const M = window.SegalNameNecklaceMetrics = window.SegalNameNecklaceMetrics || {};
            M.lastVariantIntercept = { from: v, to: String(match.id), metal, at: new Date().toISOString() };
            setTimeout(unhideActive, 0); setTimeout(unhideActive, 400); setTimeout(unhideActive, 1500);
          }
        }
      }
    } catch (_) { /* never break navigation */ }
    return origReplaceState(state, title, url);
  };

  // The theme's lazy-loader marks a slide "loaded" but leaves it at opacity .001 when it enters
  // during a swap (no `donothide`). If the slide on screen is a finished image and still invisible,
  // show it. Only ever touches the current slide's own photo, never the ribbon.
  const unhideActive = () => {
    let n = 0;
    document.querySelectorAll(`.product-gallery__main_item.slick-current ${PHOTO}, .product-gallery__main_item.slick-active ${PHOTO}`).forEach((img) => {
      if (isRibbon(img)) return;
      if (!img.complete || !img.naturalWidth) return;
      if (parseFloat(getComputedStyle(img).opacity) >= 0.5) return;
      img.classList.add('donothide'); img.style.opacity = '1'; n += 1;
    });
    return n;
  };

  setInterval(check, 900);
  setInterval(unhideActive, 700);
  window.addEventListener('popstate', check);
  // The theme's custom dropdown writes the native select and fires the change through jQuery's
  // trigger(), which no native addEventListener ever sees (jQuery only dispatches real DOM events
  // for click/focus/blur/submit). Measured 2026-09-04 (Jeanette): a real click on "14K White Gold"
  // reached none of the name-necklace scripts' change listeners, only the polling below. So the
  // label is watched, and when it changes without a native event one is dispatched on the select -
  // the switcher, this guard and the UI block then all run their normal metal-change handling.
  let lastMetalSeen = visibleMetal();
  setInterval(() => {
    const m = visibleMetal();
    if (!m || m === lastMetalSeen) return;
    lastMetalSeen = m;
    const sel = document.querySelector('#variant-selector-option-0, select.single-option-selector');
    if (!sel) return;
    const ev = new Event('change', { bubbles: true }); ev.__segalSynthetic = true;
    sel.dispatchEvent(ev);
  }, 250);

  document.addEventListener('change', () => {
    lastMetalSeen = visibleMetal();
    openIntent('metal'); patchSliders(); check(); settle();
    // metal changed while a colour is still on its way: keep the note honest for the new pick
    const m = visibleMetal();
    if (m && current && missingColours(current).includes(BASE_METAL(m))) showPending(m); else clearPending();
  }, true);
  theForm()?.addEventListener('submit', () => {
    const p = cache.get(handleFromUrl());
    if (p) fixVariant(p);
  }, true);
  patchSliders();
  check();
})();
